/**
 * Monaco Editor 语言选择器组件
 *
 * 提供下拉选择器用于切换编程语言
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SUPPORTED_LANGUAGES } from '@/constants/monaco-config'
import type { SupportedLanguage } from '@/types/monaco-editor'
import { getLanguageDisplayName, getLanguageExtension } from '@/utils/monaco-languages'

export interface LanguageSelectorProps {
  /** 当前选中的语言 */
  value: SupportedLanguage
  /** 语言变化回调 */
  onChange: (language: SupportedLanguage) => void
  /** 可选的语言列表，默认使用所有支持的语言 */
  options?: SupportedLanguage[]
  /** 是否显示搜索框 */
  showSearch?: boolean
  /** 是否显示扩展名 */
  showExtension?: boolean
  /** 占位符文本 */
  placeholder?: string
  /** 自定义类名 */
  className?: string
  /** 是否禁用 */
  disabled?: boolean
  /** 按钮尺寸 */
  size?: 'sm' | 'md' | 'lg'
  /** 最大下拉高度 */
  maxDropdownHeight?: number
  /** 语言分组方式 */
  groupBy?: 'category' | 'alphabetical' | 'none'
}

interface LanguageOption {
  value: SupportedLanguage
  label: string
  extension: string
  category: string
  searchTerm: string
}

/**
 * 语言分类
 */
const LANGUAGE_CATEGORIES = {
  // biome-ignore lint/style/useNamingConvention: <''>
  Popular: ['typescript', 'javascript', 'python', 'java', 'html', 'css', 'json'],
  // biome-ignore lint/style/useNamingConvention: <''>
  Web: ['html', 'css', 'javascript', 'typescript', 'json', 'xml', 'markdown', 'yaml'],
  // biome-ignore lint/style/useNamingConvention: <''>
  Systems: ['csharp', 'cpp', 'rust', 'go', 'kotlin', 'swift', 'objective-c'],
  // biome-ignore lint/style/useNamingConvention: <''>
  Scripting: ['python', 'ruby', 'perl', 'lua', 'bash', 'powershell'],
  'Data & Config': ['json', 'yaml', 'xml', 'sql', 'dockerfile', 'graphql', 'r'],
  // biome-ignore lint/style/useNamingConvention: <''>
  Other: ['php', 'vb', 'scala', 'dart'],
}

/**
 * 获取语言分类
 */
const getLanguageCategory = (language: SupportedLanguage): string => {
  for (const [category, languages] of Object.entries(LANGUAGE_CATEGORIES)) {
    if (languages.includes(language)) {
      return category
    }
  }
  return 'Other'
}

/**
 * 语言选择器组件
 */
export const LanguageSelector = React.forwardRef<HTMLDivElement, LanguageSelectorProps>(
  (
    {
      value,
      onChange,
      options,
      showSearch = true,
      showExtension = true,
      placeholder = 'Select language...',
      className = '',
      disabled = false,
      size = 'md',
      maxDropdownHeight = 300,
      groupBy = 'category',
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    // 准备语言选项
    const languageOptions = useMemo((): LanguageOption[] => {
      const languages = options || SUPPORTED_LANGUAGES.map(lang => lang.id)

      return languages.map(languageId => {
        const langInfo = SUPPORTED_LANGUAGES.find(lang => lang.id === languageId)
        return {
          value: languageId,
          label: langInfo?.name || getLanguageDisplayName(languageId),
          extension: getLanguageExtension(languageId),
          category: getLanguageCategory(languageId),
          searchTerm: `${languageId} ${langInfo?.name} ${langInfo?.extension} ${langInfo?.aliases?.join(' ') || ''}`.toLowerCase(),
        }
      })
    }, [options])

    // 过滤和分组语言
    const filteredAndGroupedOptions = useMemo(() => {
      let filtered = languageOptions

      // 搜索过滤
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        filtered = languageOptions.filter(option => option.searchTerm.includes(query))
      }

      // 分组
      if (groupBy === 'none') {
        return { 'All Languages': filtered }
      }

      const grouped: Record<string, LanguageOption[]> = {}

      if (groupBy === 'category') {
        // 按类别分组
        filtered.forEach(option => {
          if (!grouped[option.category]) {
            grouped[option.category] = []
          }
          grouped[option.category].push(option)
        })
      } else if (groupBy === 'alphabetical') {
        // 按首字母分组
        filtered.forEach(option => {
          const firstLetter = option.label.charAt(0).toUpperCase()
          const key = /^[A-Z]/.test(firstLetter) ? firstLetter : 'Other'
          if (!grouped[key]) {
            grouped[key] = []
          }
          grouped[key].push(option)
        })
      }

      // 排序分组内的选项
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => a.label.localeCompare(b.label))
      })

      return grouped
    }, [languageOptions, searchQuery, groupBy])

    // 获取当前选中的语言信息
    const selectedLanguage = useMemo(() => {
      return languageOptions.find(option => option.value === value)
    }, [languageOptions, value])

    // 处理语言选择
    const handleLanguageSelect = useCallback(
      (language: SupportedLanguage) => {
        onChange(language)
        setIsOpen(false)
        setSearchQuery('')
      },
      [onChange],
    )

    // 处理点击外部关闭下拉框
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false)
        }
      }

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside)
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [isOpen])

    // 打开下拉框时聚焦搜索框
    useEffect(() => {
      if (isOpen && showSearch && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus()
        }, 100)
      }
    }, [isOpen, showSearch])

    // 尺寸样式
    const sizeClasses = {
      sm: 'px-3 py-1 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-3 text-lg',
    }

    return (
      <div ref={ref} className={`relative inline-block text-left ${className}`} {...props}>
        {/* 触发按钮 */}
        <button
          type='button'
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            inline-flex items-center justify-between w-full rounded-lg border
            border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800
            text-gray-900 dark:text-gray-100 shadow-sm
            hover:bg-gray-50 dark:hover:bg-gray-700
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            transition-colors duration-200
            ${sizeClasses[size]}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <div className='flex items-center gap-2'>
            {/* 语言图标 */}
            <span className='text-lg'>
              {selectedLanguage?.value === 'html' && '🌐'}
              {selectedLanguage?.value === 'css' && '🎨'}
              {selectedLanguage?.value === 'javascript' && '⚡'}
              {selectedLanguage?.value === 'typescript' && '📘'}
              {selectedLanguage?.value === 'python' && '🐍'}
              {selectedLanguage?.value === 'java' && '☕'}
              {selectedLanguage?.value === 'go' && '🐹'}
              {selectedLanguage?.value === 'rust' && '🦀'}
              {selectedLanguage?.value === 'json' && '📄'}
              {!['html', 'css', 'javascript', 'typescript', 'python', 'java', 'go', 'rust', 'json'].includes(
                selectedLanguage?.value || '',
              ) && '💻'}
            </span>

            <span className='font-medium'>{selectedLanguage?.label || placeholder}</span>

            {showExtension && selectedLanguage && (
              <span className='text-gray-500 dark:text-gray-400 text-sm'>.{selectedLanguage.extension}</span>
            )}
          </div>

          {/* 下拉箭头 */}
          <svg
            className={`ml-2 -mr-1 h-5 w-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'
            aria-hidden='true'
          >
            <title>Dropdown arrow</title>
            <path
              fillRule='evenodd'
              d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
              clipRule='evenodd'
            />
          </svg>
        </button>

        {/* 下拉框 */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className={`
              absolute z-50 mt-1 w-full rounded-lg shadow-lg
              bg-white dark:bg-gray-800 border
              border-gray-300 dark:border-gray-600
              max-h-80 overflow-hidden
            `}
            style={{ maxHeight: maxDropdownHeight }}
          >
            {/* 搜索框 */}
            {showSearch && (
              <div className='p-3 border-b border-gray-200 dark:border-gray-700'>
                <div className='relative'>
                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                    <svg
                      className='h-5 w-5 text-gray-400'
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                      aria-hidden='true'
                    >
                      <title>Search icon</title>
                      <path
                        fillRule='evenodd'
                        d='M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </div>
                  <input
                    ref={searchInputRef}
                    type='text'
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className='block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           placeholder-gray-500 dark:placeholder-gray-400
                           focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                    placeholder='Search languages...'
                  />
                </div>
              </div>
            )}

            {/* 选项列表 */}
            <div className='max-h-60 overflow-y-auto'>
              {Object.entries(filteredAndGroupedOptions).map(([group, options]) => (
                <div key={group}>
                  {groupBy !== 'none' && options.length > 0 && (
                    <div className='px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600'>
                      {group}
                    </div>
                  )}

                  {options.map(option => (
                    <button
                      key={option.value}
                      type='button'
                      onClick={() => handleLanguageSelect(option.value)}
                      className={`
                        w-full px-3 py-2 text-left flex items-center justify-between
                        hover:bg-gray-100 dark:hover:bg-gray-700
                        focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700
                        transition-colors duration-150
                        ${option.value === value ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}
                      `}
                    >
                      <div className='flex items-center gap-2'>
                        <span className='text-lg'>
                          {option.value === 'html' && '🌐'}
                          {option.value === 'css' && '🎨'}
                          {option.value === 'javascript' && '⚡'}
                          {option.value === 'typescript' && '📘'}
                          {option.value === 'python' && '🐍'}
                          {option.value === 'java' && '☕'}
                          {option.value === 'go' && '🐹'}
                          {option.value === 'rust' && '🦀'}
                          {option.value === 'json' && '📄'}
                          {!['html', 'css', 'javascript', 'typescript', 'python', 'java', 'go', 'rust', 'json'].includes(option.value) &&
                            '💻'}
                        </span>
                        <span className='font-medium'>{option.label}</span>
                      </div>

                      <span className='text-gray-500 dark:text-gray-400 text-sm'>.{option.extension}</span>
                    </button>
                  ))}
                </div>
              ))}

              {/* 无结果提示 */}
              {Object.values(filteredAndGroupedOptions).every(options => options.length === 0) && (
                <div className='px-3 py-4 text-center text-gray-500 dark:text-gray-400'>No languages found</div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  },
)

LanguageSelector.displayName = 'LanguageSelector'
